/**
 * Tarayıcı tarafı reCAPTCHA v3.
 *
 * v3 kullanıcıya hiçbir şey sormaz: sayfa yüklendiğinde Google'ın betiği
 * arka planda davranışı izler, `execute()` çağrıldığında bir belirteç üretir.
 * Puanı **sunucu** hesaplatır (src/panel/recaptcha.ts) — tarayıcıya güvenilmez.
 *
 * Betik yalnızca site anahtarı varsa yüklenir. Anahtar yoksa (test ortamı)
 * hiçbir dış istek yapılmaz ve panel çevrimdışı da çalışır.
 */

/** Google'ın sayfaya eklediği global. */
interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

const BETIK_KIMLIGI = 'google-recaptcha-v3';

/** Aynı anahtar için ikinci kez yükleme yapılmasın. */
let yuklemeSozu: Promise<Grecaptcha> | null = null;

/**
 * Betiği yükler ve hazır olmasını bekler.
 * Yükleme başarısız olursa (ağ yok, engelleyici eklenti) hata fırlatır;
 * çağıran bunu yutar — reCAPTCHA yokluğu girişi engellemez, sunucu karar verir.
 */
export function recaptchaYukle(siteKey: string): Promise<Grecaptcha> {
  if (yuklemeSozu) return yuklemeSozu;

  yuklemeSozu = new Promise<Grecaptcha>((cozumle, reddet) => {
    // Betik zaten sayfadaysa (sıcak yeniden yükleme) tekrar ekleme.
    const mevcut = window.grecaptcha;
    if (mevcut) {
      mevcut.ready(() => cozumle(mevcut));
      return;
    }

    const betik = document.createElement('script');
    betik.id = BETIK_KIMLIGI;
    betik.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    betik.async = true;
    betik.defer = true;

    betik.onload = () => {
      const g = window.grecaptcha;
      if (!g) {
        reddet(new Error('reCAPTCHA yüklendi ama global bulunamadı'));
        return;
      }
      g.ready(() => cozumle(g));
    };
    betik.onerror = () => {
      // Bir sonraki denemede tekrar yüklenebilsin diye söz sıfırlanır.
      yuklemeSozu = null;
      reddet(new Error('reCAPTCHA betiği yüklenemedi'));
    };

    document.head.appendChild(betik);
  });

  return yuklemeSozu;
}

/**
 * Bir eylem için belirteç üretir.
 *
 * Hata durumunda `null` döner — girişi burada ENGELLEMEYİZ. Belirteç
 * gitmezse sunucu kendi kuralına göre karar verir (varsayılan: Google'a
 * ulaşılamıyorsa giriş engellenmez, ama bu kararı sunucu verir).
 */
export async function recaptchaBelirteci(siteKey: string, eylem: string): Promise<string | null> {
  try {
    const g = await recaptchaYukle(siteKey);
    return await g.execute(siteKey, { action: eylem });
  } catch {
    return null;
  }
}
